import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export interface UiPreferences {
  reducedMotion: boolean;
  coaching: boolean;
}

interface UiPreferencesContextValue {
  preferences: UiPreferences;
  setPreference: <K extends keyof UiPreferences>(key: K, value: UiPreferences[K]) => void;
}

const STORAGE_KEY = "guandan.ui-preferences.v1";
const DEFAULT_PREFERENCES: UiPreferences = { reducedMotion: false, coaching: true };
const UiPreferencesContext = createContext<UiPreferencesContextValue | null>(null);

function loadPreferences(): UiPreferences {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === null) return DEFAULT_PREFERENCES;
    const value = JSON.parse(stored) as Partial<UiPreferences>;
    return {
      reducedMotion: value.reducedMotion === true,
      coaching: value.coaching !== false,
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

/**
 * UI-only choices belong on this device, not in the account profile.  They are
 * available to every surface immediately, including a table opened in another
 * route, without adding a server-side preference contract.
 */
export function UiPreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<UiPreferences>(loadPreferences);

  useEffect(() => {
    document.documentElement.toggleAttribute("data-reduced-motion", preferences.reducedMotion);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch {
      // Private browsing can deny storage. The current-session preference still works.
    }
  }, [preferences]);

  const value = useMemo<UiPreferencesContextValue>(() => ({
    preferences,
    setPreference(key, next) {
      setPreferences((current) => ({ ...current, [key]: next }));
    },
  }), [preferences]);

  return <UiPreferencesContext.Provider value={value}>{children}</UiPreferencesContext.Provider>;
}

export function useUiPreferences(): UiPreferencesContextValue {
  const value = useContext(UiPreferencesContext);
  if (value === null) throw new Error("useUiPreferences must be used inside UiPreferencesProvider");
  return value;
}
